import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Notification from '../../../types/notifcation/Notification';
import BatteryStatus from '../../../types/physical-sample/BatteryStatus';
import PhysicalSample from '../../../types/physical-sample/PhysicalSample';
import PhysicalSampleState from '../../../types/physical-sample/PhysicalSampleState';
import VehicleBrands from '../../../types/retailer/VehicleBrands';
import VehicleModelOverview from '../../../types/retailer/VehicleModelOverview';
import Page from '../../../types/shared/Page';
import Studio from '../../../types/studio/Studio';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class RetailerService {

    private RESOURCE: string = 'retailer';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the vehicle models from the backend.
     *
     * @param search The search query to filter the vehicle models by.
     * @param bodyType The body type to filter the vehicle models by.
     * @return The vehicle models.
     */
    public async getVehicleModels(search?: string, bodyType?: string): Promise<VehicleModelOverview> {
        let params = new HttpParams();
        if (search) {
            params = params.set('search', search);
        }
        if (bodyType) {
            params = params.set('bodyType', bodyType);
        }
        params = params.set('studioId', this.getSelectedStudioId().toString());
        return await this.baseService.get<VehicleModelOverview>(this.RESOURCE + '/vehicle-models', params);
    }

    /**
     * Fetches the available brands from the backend for the given body type and car class.
     *
     * @param bodyType The body type from the vehicle model
     * @param carClass The car class from the vehicle model
     * @param modelSeries The model series from the vehicle model
     * @return The available brands
     */
    public async getAvailableBrands(bodyType: string, carClass: string, modelSeries: string): Promise<VehicleBrands> {
        let params = new HttpParams().set('bodyType', bodyType).set('carClass', carClass)
            .set('modelSeries', modelSeries);
        params = params.set('studioId', this.getSelectedStudioId().toString());
        return await this.baseService.get<VehicleBrands>(this.RESOURCE + '/brands', params);
    }

    /**
     * Select a vehicle model for sample lightíng
     */
    public async selectVehicleModel(bodyType: string, carClass: string, modelSeries: string,
                                    brand: string, manufaktur: boolean, standard: boolean,
                                    manufakturExclusive: boolean): Promise<void> {
        let params = new HttpParams().set('bodyType', bodyType).set('carClass', carClass)
            .set('modelSeries', modelSeries).set('brand', brand).set('manufaktur', manufaktur)
            .set('standard', standard).set('manufakturExclusive', manufakturExclusive);

        params = params.set('studioId', this.getSelectedStudioId().toString());
        return await this.baseService.post<void>(this.RESOURCE + '/select-vehicle-model', null, params);
    }

    /**
     * Get the studios for the logged-in user
     */
    public async getStudios(): Promise<Studio[]> {
        return await this.baseService.get<Studio[]>(this.RESOURCE + '/my-studios');
    }

    /**
     * Turn lights off
     */
    public async turnLightsOff(): Promise<void> {
        let params = new HttpParams();
        params = params.set('studioId', this.getSelectedStudioId().toString());

        return await this.baseService.post<void>(this.RESOURCE + '/lights-off', null, params);
    }

    /**
     * Fetch a paged list of PhysicalSampleDTOs from the backend.
     *
     * @param search                Optional: full‐text search string
     * @param active                Optional: whether to include only active samples
     * @param expired               Optional: whether to include only expired samples
     * @param expirationDateBefore  Optional: ISO‐date (yyyy‐MM‐dd) to filter by "expiry < this date"
     * @param sampleTypeIds         Optional: array of sample‐type IDs
     * @param sampleCategoryIds     Optional: array of sample‐category IDs
     * @param states                Optional: array of PhysicalSampleState values
     * @param batteryStatuses       Optional: array of BatteryStatus values
     * @param inDrawer              Optional: whether to include only samples that are currently in a drawer
     * @param page                  Optional: zero‐based page index (defaults to 0)
     * @param size                  Optional: page size (defaults to 20)
     * @param sort                  Optional: array of Spring‐style sort strings, e.g. ["name,asc", "id,desc"]
     * @returns Promise<Page<PhysicalSampleDTO>>
     */
    public async getSamples(
        search?: string,
        active?: boolean,
        expired?: boolean,
        expirationDateBefore?: string,      // “yyyy‐MM‐dd”
        sampleTypeIds?: number[],
        sampleCategoryIds?: number[],
        states?: PhysicalSampleState[],
        batteryStatuses?: BatteryStatus[],
        inDrawer?: boolean,
        page: number = 0,
        size: number = 20,
        sort?: string[]
    ): Promise<Page<PhysicalSample>> {

        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        if (search) {
            params = params.set('search', search);
        }
        if (active !== undefined) {
            params = params.set('active', active.toString());
        }
        if (expired !== undefined) {
            params = params.set('expired', expired.toString());
        }
        if (expirationDateBefore) {
            params = params.set('expirationDateBefore', expirationDateBefore);
        }
        if (sampleTypeIds && sampleTypeIds.length > 0) {
            sampleTypeIds.forEach((id) => {
                params = params.append('sampleTypeIds', id.toString());
            });
        }
        if (sampleCategoryIds && sampleCategoryIds.length > 0) {
            sampleCategoryIds.forEach((id) => {
                params = params.append('sampleCategoryIds', id.toString());
            });
        }
        if (states && states.length > 0) {
            states.forEach((state) => {
                params = params.append('states', state);
            });
        }
        if (batteryStatuses && batteryStatuses.length > 0) {
            batteryStatuses.forEach((status) => {
                params = params.append('batteryStatuses', status);
            });
        }
        if (inDrawer !== undefined) {
            params = params.set('inDrawer', inDrawer.toString());
        }

        // Pagination parameters (page, size, sort…)
        params = params.set('page', page.toString()).set('size', size.toString());
        if (sort && sort.length > 0) {
            sort.forEach((s) => {
                params = params.append('sort', s);
            });
        }

        return await this.baseService.get<Page<PhysicalSample>>(
            `${ this.RESOURCE }/samples`,
            params
        );
    }

    /**
     * Leave the current usecase
     */
    public async leaveUsecase(): Promise<void> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        return await this.baseService.post<void>(`${ this.RESOURCE }/leave-usecase`, null, params);
    }

    /**
     * Fetches all notifications for the current studio.
     */
    public async getNotifications(): Promise<Notification[]> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        return await this.baseService.get<Notification[]>(
            this.RESOURCE + '/notifications',
            params
        );
    }

    /**
     * Fetches the count of unread notifications for the current studio.
     */
    public async getUnreadNotificationCount(): Promise<number> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        return await this.baseService.get<number>(
            this.RESOURCE + '/notifications/unread-count',
            params
        );
    }

    /**
     * Marks a specific notification as read for the current studio.
     * @param notificationId The ID of the notification to mark as read.
     */
    public async markNotificationAsRead(notificationId: number): Promise<void> {
        let params = new HttpParams()
            .set('notificationId', notificationId.toString())
            .set('studioId', this.getSelectedStudioId().toString());

        return await this.baseService.post<void>(
            this.RESOURCE + '/mark-notification-as-read',
            null,
            params
        );
    }

    /**
     * Marks all notifications as read for the current studio.
     */
    public async markAllNotificationsAsRead(): Promise<void> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        return await this.baseService.post<void>(
            this.RESOURCE + '/mark-all-notifications-as-read',
            null,
            params
        );
    }

    /**
     * Turn lights on for specific physical samples
     *
     * @param physicalSampleIds The IDs of the physical samples to turn the lights on for
     */
    public async turnLightsOn(physicalSampleIds: number[]): Promise<void> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );
        physicalSampleIds.forEach((id) => {
            params = params.append('physicalSampleIds', id.toString());
        });

        return await this.baseService.post<void>(
            this.RESOURCE + '/turn-lights-on',
            null,
            params
        );
    }

    /**
     * Turn all lights on
     */
    public async turnAllLightsOn(): Promise<void> {
        let params = new HttpParams().set(
            'studioId',
            this.getSelectedStudioId().toString()
        );

        return await this.baseService.post<void>(
            this.RESOURCE + '/all-lights-on',
            null,
            params
        );
    }

    /**
     * Get the selected studio id
     *
     * @return The selected studio id
     */
    private getSelectedStudioId(): number {
        const selectedStudioId = localStorage.getItem('selectedStudioId');
        if (!selectedStudioId) {
            throw new Error('No studio selected');
        }
        return selectedStudioId ? parseInt(selectedStudioId) : 0;
    }

}
